import { OnDataOptions } from "@apollo/client"
import { trackAnalyticsEvent } from "@web/context/analytics/client"
import { PaymentRequiredError } from "@web/types/error/payment"
import {
  CandidateEmailOutput,
  CandidateOutput,
  CandidatePhoneNumberOutput,
} from "platform-common/api/gql/generated/graphql"
import { GQL, normalizePhoneNumber, verifyFeatureAccessWithQuotas } from "platform-common/index"
import { createContext, useCallback, useContext, useEffect, useState } from "react"
import removeAccents from "remove-accents"

export type UpdatedContactInfo = GQL.OnContactInfoAddedToCandidateSubscription["onContactInfoAddedToCandidate"]

export const ContactInfoContext = createContext<ReturnType<typeof useGetContactInfo> | null>(null)

interface UseGetContactInfoArgs {
  candidate?: Pick<GQL.CandidateOutput, "id" | "linkedInUrl" | "firstName" | "lastName"> | null
  billingStatus?: GQL.BillingStatus
  autoFetch?: boolean
  wantPhone?: boolean
  wantEmail?: boolean

  onContactInfoUpdated?: (contactInfo: UpdatedContactInfo) => void
}

export const useGetContactInfo = ({
  wantPhone,
  wantEmail,
  autoFetch,
  candidate,
  billingStatus,
  onContactInfoUpdated,
}: UseGetContactInfoArgs) => {
  const [_fetchContactInfo, { error, loading }] = GQL.useLookUpEmailAsyncV2Mutation()

  const [didLookUpEmails, setDidLookupEmails] = useState(false)
  const [didLookUpPhones, setDidLookupPhones] = useState(false)

  const [lookedUpEmails, setLookedUpEmails] = useState<GQL.CandidateEmailOutput[]>()
  const [lookedUpPhones, setLookedUpPhones] = useState<GQL.CandidatePhoneNumberOutput[]>()
  const [unsubscribed, setUnsubscribed] = useState(false)
  const [lookUpEmailTaskId, setLookUpEmailTaskId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const { hasAccess: canDoContactInfoLookup } = verifyFeatureAccessWithQuotas({
    featureName: "email_lookup",
    billingPlanFeatures: billingStatus?.enabledFeatures ?? [],
    userQuotas: billingStatus?.quotas ?? [],
  })

  const handleFetchFinished = useCallback(() => {
    setLookUpEmailTaskId(null)
    setIsLoading(false)
  }, [])

  const formatPhoneNumbers = useCallback(
    (phoneNumbers: GQL.CandidatePhoneNumberOutput[]) => {
      const formatted = phoneNumbers.map((phoneNumberData) => {
        return {
          ...phoneNumberData,
          phoneNumber: normalizePhoneNumber(phoneNumberData.phoneNumber),
        }
      })
      return formatted
    },
    [normalizePhoneNumber]
  )

  const fetchContactInfo = useCallback(async () => {
    if (!canDoContactInfoLookup) return

    setIsLoading(true)

    if (!candidate?.linkedInUrl || !candidate?.id) {
      setIsLoading(false)
      console.warn("Candidate linkedInUrl and id are required")
      return
    }

    const fetchEmailResult = await _fetchContactInfo({
      variables: {
        input: {
          candidateId: candidate.id,
          linkedInUrl: candidate.linkedInUrl,
          wantPhone: wantPhone,
          wantEmail: wantEmail,
          ...(candidate?.firstName && {
            fullName: { firstName: candidate.firstName, lastName: candidate.lastName },
          }),
        },
      },
    })

    const data = fetchEmailResult.data?.lookUpContactInfoAsyncV2
    if (!data) return

    const resultType = data?.__typename
    if (resultType === "PaymentRequiredError") {
      throw new PaymentRequiredError(data)
    }

    const contactInfo = data as GQL.LookUpEmailResponseAsync

    wantEmail && setDidLookupEmails(true)
    wantPhone && setDidLookupPhones(true)

    const taskId = contactInfo.taskId
    const emails = contactInfo.emails ?? undefined
    const unsubscribed = Boolean(contactInfo.unsubscribed)
    const phones = contactInfo.phoneNumbers ?? undefined
    const formattedPhones = phones ? formatPhoneNumbers(phones) : undefined

    setUnsubscribed(unsubscribed)
    setLookUpEmailTaskId(taskId ?? null)

    if (emails?.length || phones?.length) {
      setLookedUpEmails(emails)
      setLookedUpPhones(formattedPhones)
      onContactInfoUpdated?.({ id: candidate.id, emailAddresses: emails, phoneNumbersFull: phones })
      trackAnalytics(candidate, emails, phones)
    }

    if (!taskId) {
      handleFetchFinished()
    }
  }, [
    canDoContactInfoLookup,
    candidate,
    _fetchContactInfo,
    wantPhone,
    wantEmail,
    formatPhoneNumbers,
    onContactInfoUpdated,
    handleFetchFinished,
  ])

  /**
   * Task update handlers
   */
  const handleContactInfoAdded = useCallback(
    ({ data: { data: _data } }: OnDataOptions<GQL.OnContactInfoAddedToCandidateSubscription>) => {
      const updatedContactInfo = _data?.onContactInfoAddedToCandidate

      if (!updatedContactInfo) return

      onContactInfoUpdated?.(updatedContactInfo)

      const emails = updatedContactInfo.emailAddresses ?? undefined

      const phones = updatedContactInfo.phoneNumbersFull ?? undefined
      const formattedPhones = phones ? formatPhoneNumbers(phones) : undefined

      if (emails) setLookedUpEmails(emails)
      if (phones) setLookedUpPhones(formattedPhones)

      trackAnalytics(candidate, emails, phones)
    },
    [onContactInfoUpdated, formatPhoneNumbers, candidate]
  )

  const handleEmailLookupStatusChanged = useCallback(
    ({ data: { data: _data } }: OnDataOptions<GQL.OnTaskStatusUpdateSubscription>) => {
      const { id, status = "UNKNOWN" } = _data?.onTaskStatusUpdate || {}

      if (id == lookUpEmailTaskId && ["COMPLETED", "FAILED"].includes(status)) {
        handleFetchFinished()
      } else {
        console.debug("Got task status update but not for current task; discarding")
      }
    },
    [handleFetchFinished, lookUpEmailTaskId]
  )

  /**
   * Subscriptions
   */
  GQL.useOnTaskStatusUpdateSubscription({
    skip: !lookUpEmailTaskId,
    onData: handleEmailLookupStatusChanged,
    variables: { id: lookUpEmailTaskId! },
  })

  GQL.useOnContactInfoAddedToCandidateSubscription({
    skip: !candidate?.id,
    onData: handleContactInfoAdded,
    variables: { id: candidate?.id || "" },
  })

  useEffect(
    function autoFetchContactInfo() {
      if (autoFetch === false) return

      const shouldLookupEmails = wantEmail && !(didLookUpEmails || !!lookedUpEmails?.length)
      const shouldLookupPhones = wantPhone && !(didLookUpPhones || !!lookedUpPhones?.length)
      const shouldLookupContactInfo = shouldLookupEmails || shouldLookupPhones

      if (!shouldLookupContactInfo || error || loading || isLoading) {
        return
      }

      fetchContactInfo().catch(console.error)
    },
    [
      fetchContactInfo,
      error,
      loading,
      autoFetch,
      didLookUpEmails,
      wantEmail,
      wantPhone,
      didLookUpPhones,
      lookedUpEmails?.length,
      lookedUpPhones?.length,
      lookedUpEmails,
      lookedUpPhones,
      isLoading,
    ]
  )

  return {
    fetchContactInfo,
    unsubscribed,
    canDoContactInfoLookup,
    didLookUpEmails,
    lookedUpEmails: lookedUpEmails?.map((e) => ({ ...e, name: e.name && removeAccents(e.name) })),
    lookedUpPhones,
    isLoading,
    error,
  }
}

const trackAnalytics = (
  candidate?: Partial<CandidateOutput> | null,
  emails?: CandidateEmailOutput[],
  phones?: CandidatePhoneNumberOutput[]
) => {
  if (emails?.length) {
    trackAnalyticsEvent({
      event: `Emails Found`,
      candidateId: candidate?.id,
      privateEmailsCount: emails?.filter((e) => e.type !== "PRIVATE").length || 0,
      workEmailsCount: emails?.filter((e) => e.type !== "PRIVATE").length || 0,
    })
  }

  if (phones?.length) {
    trackAnalyticsEvent({
      event: `Phones Found`,
      candidateId: candidate?.id,
      privatePhonesCount: phones?.filter((e) => e.type === "PRIVATE").length || 0,
      workPhonesCount: phones?.filter((e) => e.type === "WORK").length || 0,
    })
  }
}

export const useContactInfoContext = () => {
  const context = useContext(ContactInfoContext)

  return context
}
